// UI 관리 클래스
export class UI {
  private alertTimeout: number | null = null;
  private loadingStack: number = 0;
  private loadingElement: HTMLElement | null;
  private modalElement: HTMLElement | null;
  private toastElement: HTMLElement | null;

  constructor() {
    this.loadingElement = document.getElementById('loading');
    this.modalElement = document.getElementById('modal');
    this.toastElement = document.getElementById('toast');
    this.initUI();
  }

  // UI 초기화
  private initUI() {
    this.hideAlert();
    this.setupTooltips();
    this.setupKeyboardShortcuts();
  }

  // 툴팁 설정
  private setupTooltips() {
    document.addEventListener('mouseover', (e) => {
      const target = e.target as HTMLElement;
      if (target.hasAttribute('data-tooltip')) {
        this.showTooltip(target, target.getAttribute('data-tooltip')!);
      }
    });

    document.addEventListener('mouseout', (e) => {
      const target = e.target as HTMLElement;
      if (target.hasAttribute('data-tooltip')) {
        this.hideTooltip();
      }
    });
  }

  // 키보드 단축키 설정
  private setupKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      // Ctrl/Cmd + K로 검색 포커스
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.getElementById('search-forms') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }

      // ESC로 모달 닫기
      if (e.key === 'Escape') {
        this.hideModal();
        this.hideTooltip();
      }
    });
  }

  // 로그인 섹션 표시
  showLoginSection() {
    const loginSection = document.getElementById('login-section');
    const dashboard = document.getElementById('dashboard');
    const navUser = document.getElementById('nav-user');
    const navGuest = document.getElementById('nav-guest');

    if (loginSection) loginSection.classList.remove('hidden');
    if (dashboard) dashboard.classList.add('hidden');
    if (navUser) navUser.classList.add('hidden');
    if (navGuest) navGuest.classList.remove('hidden');
  }

  // 대시보드 표시
  showDashboard() {
    const loginSection = document.getElementById('login-section');
    const dashboard = document.getElementById('dashboard');
    const navUser = document.getElementById('nav-user');
    const navGuest = document.getElementById('nav-guest');

    if (loginSection) loginSection.classList.add('hidden');
    if (dashboard) dashboard.classList.remove('hidden');
    if (navUser) navUser.classList.remove('hidden');
    if (navGuest) navGuest.classList.add('hidden');
  }

  // 사용자 이메일 설정
  setUserEmail(email: string) {
    const userEmailElement = document.getElementById('user-email');
    if (userEmailElement) {
      userEmailElement.textContent = email;
    }
  }

  // 알림 표시
  showAlert(message: string, type: 'success' | 'error' | 'info' = 'info') {
    // 기존 알림 제거
    this.hideAlert();

    const alertDiv = document.createElement('div');
    alertDiv.id = 'alert';
    alertDiv.className = `fixed top-4 right-4 max-w-sm w-full z-50 p-4 rounded-lg shadow-lg transform transition-all duration-300 ease-in-out`;

    // 타입별 스타일
    switch (type) {
      case 'success':
        alertDiv.className += ' bg-green-500 text-white';
        break;
      case 'error':
        alertDiv.className += ' bg-red-500 text-white';
        break;
      case 'info':
      default:
        alertDiv.className += ' bg-blue-500 text-white';
        break;
    }

    alertDiv.innerHTML = `
      <div class="flex items-center justify-between">
        <span>${message}</span>
        <button onclick="this.parentElement.parentElement.remove()" class="ml-4 text-white hover:text-gray-200">
          <i class="fas fa-times"></i>
        </button>
      </div>
    `;

    document.body.appendChild(alertDiv);

    // 3초 후 자동 제거
    this.alertTimeout = window.setTimeout(() => {
      this.hideAlert();
    }, 3000);
  }

  // 알림 숨기기
  hideAlert() {
    const existingAlert = document.getElementById('alert');
    if (existingAlert) {
      existingAlert.remove();
    }
    if (this.alertTimeout) {
      clearTimeout(this.alertTimeout);
      this.alertTimeout = null;
    }
  }

  // 모달 표시
  showModal(title: string, content: string) {
    if (this.modalElement) {
      const titleElement = document.getElementById('modal-title');
      const contentElement = document.getElementById('modal-content');
      
      if (titleElement) titleElement.textContent = title;
      if (contentElement) contentElement.innerHTML = content;
      
      this.modalElement.classList.remove('hidden');
    }
  }

  // 모달 숨기기
  hideModal() {
    if (this.modalElement) {
      this.modalElement.classList.add('hidden');
    }
  }

  // 로딩 표시/숨기기 (스택 기반으로 중첩 로딩 지원)
  showLoading(show: boolean, text?: string) {
    if (show) {
      this.loadingStack++;
    } else {
      this.loadingStack = Math.max(0, this.loadingStack - 1);
    }

    if (this.loadingElement) {
      if (this.loadingStack > 0) {
        this.loadingElement.classList.remove('hidden');
        if (text) {
          const messageElement = document.getElementById('loading-message');
          if (messageElement) {
            messageElement.textContent = text;
          }
        }
      } else {
        this.loadingElement.classList.add('hidden');
      }
    }
  }

  // 툴팁 표시
  private showTooltip(element: HTMLElement, text: string) {
    this.hideTooltip();
    
    const tooltip = document.createElement('div');
    tooltip.id = 'tooltip';
    tooltip.className = 'absolute z-50 bg-gray-900 text-white text-sm px-2 py-1 rounded shadow-lg pointer-events-none';
    tooltip.textContent = text;
    
    document.body.appendChild(tooltip);
    
    const rect = element.getBoundingClientRect();
    tooltip.style.left = `${rect.left + (rect.width / 2) - (tooltip.offsetWidth / 2)}px`;
    tooltip.style.top = `${rect.top - tooltip.offsetHeight - 5}px`;
  }

  // 툴팁 숨기기
  private hideTooltip() {
    const tooltip = document.getElementById('tooltip');
    if (tooltip) {
      tooltip.remove();
    }
  }

  // 통계 업데이트
  updateStats(stats: any) {
    const formsCount = document.getElementById('stats-forms');
    const totalSubmissions = document.getElementById('stats-total-submissions');
    const monthSubmissions = document.getElementById('stats-month-submissions');

    if (formsCount) formsCount.textContent = stats.forms_count || '0';
    if (totalSubmissions) totalSubmissions.textContent = stats.total_submissions || '0';
    if (monthSubmissions) monthSubmissions.textContent = stats.this_month_submissions || '0';
  }

  // 폼 미리보기 업데이트
  updateFormPreview(html: string) {
    const preview = document.getElementById('form-preview');
    if (preview) {
      preview.innerHTML = html;
    }
  }

  // 폼 목록 렌더링
  renderFormsList(forms: any[], callbacks: {
    onView: (formId: string) => void;
    onCopy: (formId: string) => void;
    onDelete: (formId: string) => void;
  }) {
    const formsList = document.getElementById('forms-list');
    const formsEmpty = document.getElementById('forms-empty');

    if (!formsList || !formsEmpty) return;

    if (forms.length === 0) {
      formsList.innerHTML = '';
      formsEmpty.classList.remove('hidden');
      return;
    }

    formsEmpty.classList.add('hidden');
    
    formsList.innerHTML = forms.map(form => `
      <div class="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
        <div class="flex justify-between items-start">
          <div class="flex-1">
            <h3 class="text-lg font-medium text-gray-900">${form.name}</h3>
            ${form.description ? `<p class="text-sm text-gray-600 mt-1">${form.description}</p>` : ''}
            <div class="flex items-center space-x-4 mt-2 text-sm text-gray-500">
              <span>
                <i class="fas fa-envelope mr-1"></i>
                ${form.target_email}
              </span>
              <span>
                <i class="fas fa-paper-plane mr-1"></i>
                ${form.submit_count}회 제출
              </span>
              <span>
                <i class="fas fa-calendar mr-1"></i>
                ${new Date(form.created_at).toLocaleDateString('ko-KR')}
              </span>
            </div>
          </div>
          <div class="flex space-x-2 ml-4">
            <button onclick="app.viewForm('${form.form_id}')" 
                    class="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    title="상세보기">
              <i class="fas fa-eye"></i>
            </button>
            <button onclick="app.copyFormCode('${form.form_id}')" 
                    class="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                    title="코드 복사">
              <i class="fas fa-copy"></i>
            </button>
            <button onclick="alert('폼 삭제 기능은 아직 구현되지 않았습니다.')" 
                    class="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                    title="삭제">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>
        
        <div class="mt-3 pt-3 border-t border-gray-100">
          <div class="flex items-center justify-between text-xs text-gray-500">
            <span class="font-mono">ID: ${form.form_id}</span>
            <span class="${form.is_active ? 'text-green-600' : 'text-red-600'}">
              <i class="fas fa-circle mr-1"></i>
              ${form.is_active ? '활성' : '비활성'}
            </span>
          </div>
        </div>
      </div>
    `).join('');
  }

  // 제출 기록 렌더링
  renderSubmissionsList(submissions: any[]) {
    const submissionsList = document.getElementById('submissions-list');
    const submissionsEmpty = document.getElementById('submissions-empty');

    if (!submissionsList || !submissionsEmpty) return;

    if (submissions.length === 0) {
      submissionsList.innerHTML = '';
      submissionsEmpty.classList.remove('hidden');
      return;
    }

    submissionsEmpty.classList.add('hidden');
    
    submissionsList.innerHTML = submissions.map(submission => {
      const submissionData = JSON.parse(submission.submission_data);
      const previewData = Object.entries(submissionData)
        .slice(0, 3)
        .map(([key, value]) => `${key}: ${String(value).substring(0, 50)}${String(value).length > 50 ? '...' : ''}`)
        .join(', ');

      return `
        <div class="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
          <div class="flex justify-between items-start">
            <div class="flex-1">
              <div class="flex items-center space-x-2 mb-2">
                <h4 class="text-md font-medium text-gray-900">${submission.form_name}</h4>
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  submission.email_sent ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }">
                  ${submission.email_sent ? '✓ 이메일 전송됨' : '✗ 이메일 실패'}
                </span>
              </div>
              <p class="text-sm text-gray-600 mb-2">${previewData}</p>
              <div class="flex items-center space-x-4 text-xs text-gray-500">
                <span>
                  <i class="fas fa-calendar mr-1"></i>
                  ${new Date(submission.created_at).toLocaleString('ko-KR')}
                </span>
                <span>
                  <i class="fas fa-map-marker mr-1"></i>
                  ${submission.sender_ip}
                </span>
              </div>
            </div>
            <button onclick="this.viewSubmission(${submission.id})" 
                    class="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    title="상세보기">
              <i class="fas fa-eye"></i>
            </button>
          </div>
        </div>
      `;
    }).join('');
  }

  // 제출 상세 보기
  viewSubmission(submissionId: number) {
    // API 호출하여 제출 상세 정보 표시
    console.log('View submission:', submissionId);
  }

  // 검색 기능
  setupSearch() {
    const searchInput = document.getElementById('search-forms') as HTMLInputElement;
    if (!searchInput) return;

    let searchTimeout: number;
    
    searchInput.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      searchTimeout = window.setTimeout(() => {
        const query = (e.target as HTMLInputElement).value.toLowerCase();
        this.filterForms(query);
      }, 300);
    });
  }

  // 폼 필터링
  private filterForms(query: string) {
    const formItems = document.querySelectorAll('#forms-list > div');
    
    formItems.forEach(item => {
      const text = item.textContent?.toLowerCase() || '';
      if (text.includes(query)) {
        (item as HTMLElement).style.display = 'block';
      } else {
        (item as HTMLElement).style.display = 'none';
      }
    });
  }

  // 다크 모드 토글
  toggleDarkMode() {
    const isDark = document.documentElement.classList.toggle('dark');
    localStorage.setItem('darkMode', isDark.toString());
    
    this.showAlert(
      isDark ? '다크 모드가 활성화되었습니다' : '라이트 모드가 활성화되었습니다',
      'info'
    );
  }

  // 다크 모드 초기화
  initDarkMode() {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode === 'true') {
      document.documentElement.classList.add('dark');
    }
  }

  // 애니메이션 효과
  animateElement(element: HTMLElement, animation: string, duration: number = 500) {
    element.style.animation = `${animation} ${duration}ms ease-in-out`;
    
    setTimeout(() => {
      element.style.animation = '';
    }, duration);
  }

  // 카피보드 복사 유틸리티
  async copyToClipboard(text: string, successMessage?: string) {
    try {
      await navigator.clipboard.writeText(text);
      this.showAlert(successMessage || '클립보드에 복사되었습니다!', 'success');
    } catch (error) {
      console.error('Clipboard copy failed:', error);
      this.showAlert('복사에 실패했습니다.', 'error');
    }
  }

  // 폼 검증 UI
  validateFormField(fieldId: string, isValid: boolean, errorMessage?: string) {
    const field = document.getElementById(fieldId) as HTMLInputElement;
    const errorElement = document.getElementById(`${fieldId}-error`);
    
    if (!field) return;

    if (isValid) {
      field.classList.remove('border-red-500');
      field.classList.add('border-green-500');
      if (errorElement) {
        errorElement.textContent = '';
        errorElement.classList.add('hidden');
      }
    } else {
      field.classList.remove('border-green-500');
      field.classList.add('border-red-500');
      if (errorElement && errorMessage) {
        errorElement.textContent = errorMessage;
        errorElement.classList.remove('hidden');
      }
    }
  }

  // 진행률 표시
  updateProgress(percentage: number, text?: string) {
    const progressBar = document.getElementById('progress-bar');
    const progressText = document.getElementById('progress-text');
    
    if (progressBar) {
      progressBar.style.width = `${percentage}%`;
    }
    
    if (progressText && text) {
      progressText.textContent = text;
    }
  }

  // 폼 필드 초기화
  clearFormFields() {
    const fields = [
      'form-name',
      'form-email', 
      'form-description',
      'form-prompt'
    ];

    fields.forEach(fieldId => {
      const field = document.getElementById(fieldId) as HTMLInputElement | HTMLTextAreaElement;
      if (field) {
        field.value = '';
      }
    });
  }

  // 로그인 필드 초기화
  clearLoginFields() {
    const loginApiKey = document.getElementById('login-apikey') as HTMLInputElement;
    if (loginApiKey) {
      loginApiKey.value = '';
    }
  }

  // 등록 필드 초기화
  clearRegisterFields() {
    const fields = ['register-email', 'register-name'];
    fields.forEach(fieldId => {
      const field = document.getElementById(fieldId) as HTMLInputElement;
      if (field) {
        field.value = '';
      }
    });
  }

  // 토스트 알림 표시
  showToast(message: string, type: 'success' | 'error' | 'info' = 'info') {
    if (this.toastElement) {
      const iconElement = document.getElementById('toast-icon');
      const messageElement = document.getElementById('toast-message');
      
      if (messageElement) messageElement.textContent = message;
      
      if (iconElement) {
        let iconClass = '';
        let iconColor = '';
        
        switch (type) {
          case 'success':
            iconClass = 'fas fa-check-circle';
            iconColor = 'text-green-500';
            break;
          case 'error':
            iconClass = 'fas fa-exclamation-circle';
            iconColor = 'text-red-500';
            break;
          default:
            iconClass = 'fas fa-info-circle';
            iconColor = 'text-blue-500';
        }
        
        iconElement.innerHTML = `<i class="${iconClass} ${iconColor}"></i>`;
      }
      
      this.toastElement.classList.remove('hidden');
      
      // 3초 후 자동 숨기기
      setTimeout(() => {
        this.hideToast();
      }, 3000);
    }
  }

  // 토스트 숨기기
  hideToast() {
    if (this.toastElement) {
      this.toastElement.classList.add('hidden');
    }
  }

  // 연결 상태 업데이트
  updateConnectionStatus(connected: boolean) {
    const statusElement = document.getElementById('connection-status');
    if (statusElement) {
      if (connected) {
        statusElement.innerHTML = `
          <i class="fas fa-circle text-green-500 mr-1"></i>
          연결됨
        `;
      } else {
        statusElement.innerHTML = `
          <i class="fas fa-circle text-red-500 mr-1"></i>
          연결 끊김
        `;
      }
    }
  }

  // 확인 대화상자
  showConfirm(message: string, onConfirm: () => void, onCancel?: () => void) {
    this.showModal('확인', `
      <div class="space-y-4">
        <p class="text-gray-700">${message}</p>
        <div class="flex space-x-3">
          <button id="confirm-yes" class="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700">
            확인
          </button>
          <button id="confirm-no" class="flex-1 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700">
            취소
          </button>
        </div>
      </div>
    `);

    // 이벤트 리스너 추가
    const yesButton = document.getElementById('confirm-yes');
    const noButton = document.getElementById('confirm-no');

    if (yesButton) {
      yesButton.addEventListener('click', () => {
        this.hideModal();
        onConfirm();
      });
    }

    if (noButton) {
      noButton.addEventListener('click', () => {
        this.hideModal();
        if (onCancel) onCancel();
      });
    }
  }

  // 입력 대화상자
  showPrompt(message: string, defaultValue: string = '', onConfirm: (value: string) => void, onCancel?: () => void) {
    this.showModal('입력', `
      <div class="space-y-4">
        <p class="text-gray-700">${message}</p>
        <input type="text" id="prompt-input" value="${defaultValue}" 
               class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
        <div class="flex space-x-3">
          <button id="prompt-ok" class="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700">
            확인
          </button>
          <button id="prompt-cancel" class="flex-1 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700">
            취소
          </button>
        </div>
      </div>
    `);

    const inputElement = document.getElementById('prompt-input') as HTMLInputElement;
    const okButton = document.getElementById('prompt-ok');
    const cancelButton = document.getElementById('prompt-cancel');

    if (inputElement) {
      inputElement.focus();
      inputElement.select();
    }

    if (okButton) {
      okButton.addEventListener('click', () => {
        const value = inputElement ? inputElement.value.trim() : '';
        this.hideModal();
        onConfirm(value);
      });
    }

    if (cancelButton) {
      cancelButton.addEventListener('click', () => {
        this.hideModal();
        if (onCancel) onCancel();
      });
    }

    // Enter 키로 확인
    if (inputElement) {
      inputElement.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          const value = inputElement.value.trim();
          this.hideModal();
          onConfirm(value);
        }
      });
    }
  }

  // 폼 검증
  validateForm(formId: string): boolean {
    const form = document.getElementById(formId) as HTMLFormElement;
    if (!form) return false;

    const inputs = form.querySelectorAll('input[required], textarea[required], select[required]');
    let isValid = true;

    inputs.forEach(input => {
      const element = input as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (!element.value.trim()) {
        element.classList.add('border-red-500');
        isValid = false;
      } else {
        element.classList.remove('border-red-500');
      }
    });

    return isValid;
  }

  // 폼 초기화
  resetForm(formId: string) {
    const form = document.getElementById(formId) as HTMLFormElement;
    if (form) {
      form.reset();
      // 에러 스타일 제거
      const inputs = form.querySelectorAll('input, textarea, select');
      inputs.forEach(input => {
        input.classList.remove('border-red-500');
      });
    }
  }

  // 요소 표시/숨기기
  toggleElement(elementId: string, show: boolean) {
    const element = document.getElementById(elementId);
    if (element) {
      if (show) {
        element.classList.remove('hidden');
      } else {
        element.classList.add('hidden');
      }
    }
  }

  // 텍스트 업데이트
  updateText(elementId: string, text: string) {
    const element = document.getElementById(elementId);
    if (element) {
      element.textContent = text;
    }
  }

  // HTML 업데이트
  updateHTML(elementId: string, html: string) {
    const element = document.getElementById(elementId);
    if (element) {
      element.innerHTML = html;
    }
  }

  // 클래스 토글
  toggleClass(elementId: string, className: string, add: boolean) {
    const element = document.getElementById(elementId);
    if (element) {
      if (add) {
        element.classList.add(className);
      } else {
        element.classList.remove(className);
      }
    }
  }

  // 스크롤 상단으로
  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // 요소로 스크롤
  scrollToElement(elementId: string) {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }
} 